/**
 * Company permission catalogue (SC-439): the one list of what a member can be allowed to do.
 *
 * Cloud code can't import types/, so it gets a hand-copied version with a parity test. Plain data
 * and `as const` only (no enum or namespace) keeps that copy a line-for-line port.
 * `server/__tests__/rbacCatalogue.test.ts` pins the full list and the default role matrix.
 *
 * Not the plan entitlements in `server/services/permissionService.ts`: those gate what a company's
 * subscription tier includes. These gate what a member may do inside the company.
 */

/** Display order of the groups. */
export const PERMISSION_GROUPS = [
  'Office',
  'Guests & bookings',
  'Members',
  'Roles & permissions',
  'Workspace',
  'Billing',
  'Compliance',
  'Ownership',
] as const

export type PermissionGroup = (typeof PERMISSION_GROUPS)[number]

/**
 * Roles every company has. Custom roles may be added later, so a role key is a plain string
 * everywhere except here.
 */
export const SYSTEM_ROLE_KEYS = ['owner', 'admin', 'member'] as const

export type SystemRoleKey = (typeof SYSTEM_ROLE_KEYS)[number]

/**
 * A company has at least one active Owner and may have several, and each holds every key. The role
 * is never assigned through a member's access: an Owner hands over by a transfer (and becomes an
 * Admin), and a platform admin adds, demotes and replaces Owners through the platform owner routes.
 */
export const OWNER_ROLE_KEY = 'owner'

export interface PermissionEntry {
  key: PermissionKey
  group: PermissionGroup
  /** Completes "You need <label> permission" in the UI. */
  label: string
  description: string
  /** False for the owner-only keys: no role or override can give them to anyone else. */
  grantable: boolean
  /** No code enforces it yet. It stays listed so roles can hold it before its feature lands. */
  reserved: boolean
  /** Ascending in catalogue order; gaps leave room to slot a key in without renumbering rows. */
  sortOrder: number
  defaultRoles: readonly SystemRoleKey[]
}

const EVERYONE = ['owner', 'admin', 'member'] as const
const OWNER_AND_ADMIN = ['owner', 'admin'] as const
const OWNER_ONLY = ['owner'] as const

const ENTRIES = [
  {
    key: 'office.use',
    group: 'Office',
    label: 'Office access',
    description: 'Move around the office, join rooms and set your status.',
    grantable: true,
    reserved: false,
    sortOrder: 10,
    defaultRoles: EVERYONE,
  },
  {
    key: 'whiteboard.use',
    group: 'Office',
    label: 'Whiteboard',
    description: 'Draw and write on whiteboards in rooms.',
    grantable: true,
    reserved: false,
    sortOrder: 20,
    defaultRoles: EVERYONE,
  },
  {
    key: 'chat.use',
    group: 'Office',
    label: 'Chat',
    description: 'Send and read chat messages.',
    grantable: true,
    reserved: false,
    sortOrder: 30,
    defaultRoles: EVERYONE,
  },
  {
    key: 'rooms.claim_owner',
    group: 'Office',
    label: 'Claim room ownership',
    description: 'Claim and hand over ownership of a meeting room.',
    grantable: true,
    reserved: false,
    sortOrder: 40,
    defaultRoles: EVERYONE,
  },
  {
    key: 'video.use',
    group: 'Office',
    label: 'Video calls',
    description: 'Use your camera and microphone in rooms.',
    grantable: true,
    reserved: false,
    sortOrder: 50,
    defaultRoles: EVERYONE,
  },
  {
    key: 'screenshare.use',
    group: 'Office',
    label: 'Screen sharing',
    description: 'Share your screen in a room.',
    grantable: true,
    reserved: false,
    sortOrder: 60,
    defaultRoles: EVERYONE,
  },
  {
    key: 'custom_rooms.create',
    group: 'Office',
    label: 'Create custom rooms',
    description: 'Create your own meeting rooms.',
    grantable: true,
    reserved: false,
    sortOrder: 70,
    defaultRoles: EVERYONE,
  },
  {
    key: 'guests.invite',
    group: 'Guests & bookings',
    label: 'Invite guests',
    description: 'Invite people from outside the company to a meeting.',
    grantable: true,
    reserved: false,
    sortOrder: 80,
    defaultRoles: EVERYONE,
  },
  {
    key: 'bookings.create',
    group: 'Guests & bookings',
    label: 'Create bookings',
    description: 'Book a room and schedule meetings.',
    grantable: true,
    reserved: false,
    sortOrder: 90,
    defaultRoles: EVERYONE,
  },
  {
    key: 'members.view',
    group: 'Members',
    label: 'View members',
    description: 'See everyone in the company and their roles.',
    grantable: true,
    reserved: false,
    sortOrder: 100,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'members.invite',
    group: 'Members',
    label: 'Invite members',
    description: 'Invite new people to join the company.',
    grantable: true,
    reserved: false,
    sortOrder: 110,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'members.edit',
    group: 'Members',
    label: 'Edit members',
    description: "Change a member's department and job title.",
    grantable: true,
    reserved: false,
    sortOrder: 120,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'members.remove',
    group: 'Members',
    label: 'Remove members',
    description: 'Remove people from the company.',
    grantable: true,
    reserved: false,
    sortOrder: 130,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'roles.view',
    group: 'Roles & permissions',
    label: 'View roles',
    description: 'See the roles and what each one allows.',
    grantable: true,
    reserved: false,
    sortOrder: 140,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'roles.assign',
    group: 'Roles & permissions',
    label: 'Assign roles',
    description: 'Change which role a member has.',
    grantable: true,
    reserved: false,
    sortOrder: 150,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'permissions.manage',
    group: 'Roles & permissions',
    label: 'Manage permissions',
    description: 'Allow or block individual permissions for a member.',
    grantable: true,
    reserved: false,
    sortOrder: 160,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'rooms.manage',
    group: 'Workspace',
    label: 'Manage rooms',
    description: "Edit the company's rooms and their settings.",
    grantable: true,
    reserved: false,
    sortOrder: 170,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'map.edit',
    group: 'Workspace',
    label: 'Edit the map',
    description: 'Change the office layout in the map editor.',
    grantable: true,
    reserved: false,
    sortOrder: 180,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'townhall.assign',
    group: 'Workspace',
    label: 'Assign town hall presenters',
    description: 'Choose who presents on the town hall stage.',
    grantable: true,
    reserved: true,
    sortOrder: 190,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'workspace.settings.manage',
    group: 'Workspace',
    label: 'Manage workspace settings',
    description: 'Change company settings and branding.',
    grantable: true,
    reserved: false,
    sortOrder: 200,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'email_templates.manage',
    group: 'Workspace',
    label: 'Manage email templates',
    description: 'Edit the emails the company sends.',
    grantable: true,
    reserved: false,
    sortOrder: 210,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'analytics.view',
    group: 'Workspace',
    label: 'View analytics',
    description: 'See usage and activity reports for the company.',
    grantable: true,
    reserved: false,
    sortOrder: 220,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'billing.view_plan',
    group: 'Billing',
    label: 'View plan',
    description: "See the company's current plan and usage.",
    grantable: true,
    reserved: false,
    sortOrder: 230,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'billing.view_invoices',
    group: 'Billing',
    label: 'View invoices',
    description: 'See and download invoices and payment history.',
    grantable: true,
    reserved: false,
    sortOrder: 240,
    defaultRoles: OWNER_ONLY,
  },
  {
    key: 'billing.manage_plan',
    group: 'Billing',
    label: 'Manage plan',
    description: "Change or cancel the company's plan.",
    grantable: true,
    reserved: false,
    sortOrder: 250,
    defaultRoles: OWNER_ONLY,
  },
  {
    key: 'wallet.manage',
    group: 'Billing',
    label: 'Manage wallet',
    description: "Add funds to and manage the company's wallet.",
    grantable: true,
    reserved: false,
    sortOrder: 260,
    defaultRoles: OWNER_ONLY,
  },
  {
    key: 'billing.notifications',
    group: 'Billing',
    label: 'Receive billing emails',
    description: "Get this company's billing emails.",
    grantable: true,
    reserved: false,
    sortOrder: 270,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'gdpr.manage_requests',
    group: 'Compliance',
    label: 'Manage data requests',
    description: 'Handle data access and deletion requests.',
    grantable: true,
    reserved: false,
    sortOrder: 280,
    defaultRoles: OWNER_AND_ADMIN,
  },
  {
    key: 'workspace.transfer_ownership',
    group: 'Ownership',
    label: 'Transfer ownership',
    description: 'Hand the company over to another member.',
    grantable: false,
    reserved: false,
    sortOrder: 290,
    defaultRoles: OWNER_ONLY,
  },
  {
    key: 'workspace.delete',
    group: 'Ownership',
    label: 'Delete workspace',
    description: 'Permanently delete the company.',
    grantable: false,
    reserved: true,
    sortOrder: 300,
    defaultRoles: OWNER_ONLY,
  },
] as const

export type PermissionKey = (typeof ENTRIES)[number]['key']

/** In display order; every consumer that returns a list of keys uses this order. */
export const PERMISSION_CATALOGUE: readonly PermissionEntry[] = ENTRIES

export const PERMISSION_KEYS: readonly PermissionKey[] = PERMISSION_CATALOGUE.map((e) => e.key)

const ENTRY_BY_KEY = new Map<string, PermissionEntry>(PERMISSION_CATALOGUE.map((e) => [e.key, e]))

/** The one place that decides whether a string from a request or a database row is a real key. */
export function isPermissionKey(value: unknown): value is PermissionKey {
  return typeof value === 'string' && ENTRY_BY_KEY.has(value)
}

export function getPermissionEntry(key: unknown): PermissionEntry | undefined {
  return typeof key === 'string' ? ENTRY_BY_KEY.get(key) : undefined
}

function defaultsFor(role: SystemRoleKey): PermissionKey[] {
  return PERMISSION_CATALOGUE.filter((e) => e.defaultRoles.includes(role)).map((e) => e.key)
}

/** What each system role starts with. The Owner's list is every key, owner-only ones included. */
export const SYSTEM_ROLE_PERMISSIONS: Readonly<Record<SystemRoleKey, readonly PermissionKey[]>> = {
  owner: defaultsFor('owner'),
  admin: defaultsFor('admin'),
  member: defaultsFor('member'),
}
